import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PublicPageLayout } from '@/components/landing/PublicPageLayout';
import { Container, KeystoneMark } from '@/components/g5';
import { LabelChip, StatPill, CourseDivider } from '@/components/aura';
import {
  ExternalLink,
  ChevronDown,
  ChevronUp,
  BookOpen,
  ClipboardCheck,
  FileText,
  Search,
  Shield,
  Award,
  Clock,
  Users,
  Home,
  GraduationCap,
} from 'lucide-react';
import { CTABanner } from '@/components/landing/CTABanner';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'Resources — Ashlar App',
  description: 'Homeowner guides, career resources, and industry links for the NJ appraisal community.',
  url: 'https://ashlarapp.com/resources',
};

const card = { background: 'var(--g5-card)', border: '1px solid var(--g5-line)' } as const;
const ink = { color: 'var(--g5-ink)' } as const;
const sub = { color: 'var(--g5-sub)' } as const;
const softBox = { background: 'var(--g5-soft)' } as const;
const line = 'var(--g5-line)';
const accent = 'var(--g5-accent)';
const tint = 'var(--g5-tint)';

/* ─── Keystone bullet ─── */
function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2.5 leading-relaxed">
      <KeystoneMark size={13} className="mt-1" />
      <span>{children}</span>
    </li>
  );
}

/* ─── Icon chip — flat brand tint ─── */
function IconChip({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl"
      style={{ background: tint, color: accent }}
    >
      {children}
    </span>
  );
}

/* ─── Expandable Guide Card ─── */
function GuideCard({
  icon,
  title,
  blurb,
  defaultOpen = false,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  blurb?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="res-card overflow-hidden rounded-3xl" style={card}>
        <CollapsibleTrigger asChild>
          <button className="group flex w-full cursor-pointer items-start gap-4 p-6 text-left">
            <IconChip>{icon}</IconChip>
            <div className="min-w-0 flex-1">
              <h3 className="font-display text-lg font-bold tracking-tight" style={ink}>{title}</h3>
              {blurb && <p className="mt-1 text-sm leading-relaxed" style={sub}>{blurb}</p>}
            </div>
            <span className="mt-2.5 shrink-0" style={sub}>
              {open ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </span>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="space-y-3 px-6 pb-6 pt-0 text-sm leading-relaxed md:pl-[5rem]" style={sub}>
            {children}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

/* ─── External Link Card ─── */
function ExternalLinkCard({ href, title, description }: { href: string; title: string; description: string }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="group block h-full">
      <div className="res-card h-full rounded-3xl p-6" style={card}>
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-display font-bold tracking-tight" style={ink}>{title}</h3>
          <ExternalLink className="mt-0.5 h-4 w-4 shrink-0" style={{ color: accent }} strokeWidth={1.75} />
        </div>
        <p className="mt-1.5 text-sm leading-relaxed" style={sub}>{description}</p>
      </div>
    </a>
  );
}

/* ─── Audience split card (homeowners vs appraisers) ─── */
function AudienceCard({
  icon,
  eyebrow,
  title,
  description,
  topics,
  href,
}: {
  icon: React.ReactNode;
  eyebrow: string;
  title: string;
  description: string;
  topics: string[];
  href: string;
}) {
  return (
    <a href={href} className="group block h-full">
      <div className="res-card flex h-full flex-col rounded-3xl p-7" style={card}>
        <IconChip>{icon}</IconChip>
        <p className="mt-4 font-mono text-xs uppercase tracking-[0.16em]" style={{ color: accent }}>{eyebrow}</p>
        <h3 className="mt-2 font-display text-xl font-bold tracking-tight" style={ink}>{title}</h3>
        <p className="mt-2 text-sm leading-relaxed" style={sub}>{description}</p>
        <ul className="mt-5 space-y-2.5 text-sm" style={ink}>
          {topics.map((t) => <Bullet key={t}>{t}</Bullet>)}
        </ul>
        <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: accent }}>
          Jump to guides
          <KeystoneMark size={13} />
        </span>
      </div>
    </a>
  );
}

export default function Resources() {
  return (
    <PublicPageLayout
      title="Resources"
      description="Homeowner guides, career resources, and industry links for the NJ appraisal community."
      breadcrumbLabel="Resources"
      canonicalPath="/resources"
      jsonLd={jsonLd}
    >
      {/* Hero + audience split */}
      <section className="relative overflow-hidden" style={{ borderBottom: `1px solid ${line}` }}>
        <Container className="relative py-16 md:py-24">
          <LabelChip>Resources</LabelChip>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight md:text-5xl" style={ink}>
            Knowledge hub.
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed" style={sub}>
            Whether you're a homeowner preparing for an appraisal or a professional building your career, we've got you covered.
          </p>
          <div className="mt-8 flex flex-wrap gap-2.5">
            <StatPill value="9" label="in-depth guides" />
            <StatPill value="NJ" label="licensing path" />
            <StatPill value="6" label="industry links" />
          </div>

          {/* For homeowners vs for appraisers */}
          <div className="mt-12 grid gap-5 md:grid-cols-2">
            <AudienceCard
              href="#homeowners"
              icon={<Home className="h-5 w-5" strokeWidth={1.75} />}
              eyebrow="For homeowners"
              title="Preparing for an appraisal"
              description="Know what to expect, how to prepare your home, and how to read the report your lender receives."
              topics={['What happens during the visit', 'How to prepare your home', 'Reading your appraisal report']}
            />
            <AudienceCard
              href="#appraisers"
              icon={<GraduationCap className="h-5 w-5" strokeWidth={1.75} />}
              eyebrow="For appraisers"
              title="Starting and growing your career"
              description="The NJ licensing path, credential tiers, finding a supervisor, and keeping your license current."
              topics={['Becoming licensed in NJ', 'Credential tiers explained', 'Finding a supervisory appraiser']}
            />
          </div>
        </Container>
      </section>

      {/* ──────────────── HOMEOWNER SECTION ──────────────── */}
      <section id="homeowners" className="relative overflow-hidden scroll-mt-20">
        <Container className="py-16 md:py-24">
          <div className="max-w-2xl">
            <LabelChip>Homeowner guides</LabelChip>
            <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight md:text-4xl" style={ink}>
              For homeowners.
            </h2>
            <p className="mt-4 text-base leading-relaxed" style={sub}>
              Everything you need to know about the appraisal process — from scheduling to the final value opinion.
            </p>
          </div>

          <div className="mt-10 space-y-4">
            <GuideCard
              icon={<ClipboardCheck className="h-5 w-5" strokeWidth={1.75} />}
              title="What to expect during an appraisal"
              blurb="The five steps, and how long each one takes."
              defaultOpen
            >
              <p>A residential appraisal typically takes <strong style={ink}>2–4 hours</strong> for the inspection and <strong style={ink}>5–10 business days</strong> for the completed report. Here's the typical process:</p>
              <ol className="mt-2 list-decimal space-y-2 pl-4">
                <li><strong style={ink}>Scheduling</strong> — Your lender or the appraiser will contact you to set up an inspection time. The homeowner (or their agent) should be present to provide access.</li>
                <li><strong style={ink}>Exterior inspection</strong> — The appraiser photographs the front, rear, and street scene. They measure the exterior dimensions to calculate gross living area (GLA) and note the condition of the roof, siding, landscaping, and any outbuildings.</li>
                <li><strong style={ink}>Interior inspection</strong> — Room-by-room walkthrough noting the number of bedrooms, bathrooms, kitchen condition, flooring, mechanical systems (HVAC, plumbing, electrical), and any updates or renovations.</li>
                <li><strong style={ink}>Market research</strong> — After leaving your home, the appraiser researches recent comparable sales, pending sales, and active listings in your neighborhood. This is the most time-intensive part of the process.</li>
                <li><strong style={ink}>Report writing</strong> — The appraiser analyzes the data, makes adjustments for differences between your home and the comparables, and arrives at a final value opinion. The completed report is delivered to your lender.</li>
              </ol>
            </GuideCard>

            <GuideCard
              icon={<Search className="h-5 w-5" strokeWidth={1.75} />}
              title="How to prepare your home"
              blurb="Practical do's and don'ts before the appraiser arrives."
            >
              <p>While an appraisal isn't the same as staging your home for sale, first impressions still matter. Here are practical steps:</p>
              <div className="mt-2 grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl p-4" style={softBox}>
                  <p className="mb-2 font-semibold" style={ink}>Do</p>
                  <ul className="space-y-1.5">
                    <Bullet>Ensure all areas of the home are accessible (attic, basement, crawl space, garage)</Bullet>
                    <Bullet>Compile a list of improvements with dates and approximate costs</Bullet>
                    <Bullet>Gather permits for any renovations or additions</Bullet>
                    <Bullet>Make minor repairs — fix leaky faucets, replace broken fixtures, patch holes</Bullet>
                    <Bullet>Clean and declutter so the appraiser can see rooms clearly</Bullet>
                    <Bullet>Note any energy-efficient upgrades (solar, insulation, windows)</Bullet>
                  </ul>
                </div>
                <div className="rounded-2xl p-4" style={softBox}>
                  <p className="mb-2 font-semibold" style={ink}>Don't</p>
                  <ul className="space-y-1.5">
                    <Bullet>Follow the appraiser around — let them work independently</Bullet>
                    <Bullet>Try to influence the value with comparable sales you've selected</Bullet>
                    <Bullet>Hide problems — concealment undermines trust, and they're trained to find them</Bullet>
                    <Bullet>Invest in major renovations just for the appraisal — ROI varies significantly</Bullet>
                    <Bullet>Panic about clutter — appraisers look past normal living conditions</Bullet>
                  </ul>
                </div>
              </div>
            </GuideCard>

            <GuideCard
              icon={<FileText className="h-5 w-5" strokeWidth={1.75} />}
              title="Understanding your appraisal report"
              blurb="The four sections that matter most to you."
            >
              <p>Your appraisal report is a detailed document, typically 20–40 pages. Here are the key sections to focus on:</p>
              <div className="mt-2 space-y-3">
                <div className="rounded-2xl p-4" style={softBox}>
                  <p className="font-semibold" style={ink}>Subject section</p>
                  <p>Your property's physical details — address, lot size, GLA, room count, condition, and quality ratings. Review this carefully for accuracy.</p>
                </div>
                <div className="rounded-2xl p-4" style={softBox}>
                  <p className="font-semibold" style={ink}>Neighborhood analysis</p>
                  <p>Market conditions in your area including price trends, supply/demand balance, and predominant property types. This context supports the final value.</p>
                </div>
                <div className="rounded-2xl p-4" style={softBox}>
                  <p className="font-semibold" style={ink}>Sales comparison grid</p>
                  <p>The heart of the report. Shows 3–6 comparable sales with line-by-line adjustments for differences in size, condition, features, and location. The adjusted values bracket your home's estimated value.</p>
                </div>
                <div className="rounded-2xl p-4" style={softBox}>
                  <p className="font-semibold" style={ink}>Reconciliation &amp; final value</p>
                  <p>The appraiser weighs all the data and arrives at a single "as-is" market value opinion. This is the number your lender uses for financing decisions.</p>
                </div>
              </div>
            </GuideCard>

            <GuideCard
              icon={<Shield className="h-5 w-5" strokeWidth={1.75} />}
              title="Appraisal vs. home inspection"
              blurb="Two very different services that are often confused."
            >
              <p>These are two very different services that are often confused:</p>
              <div className="mt-2 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${line}` }}>
                      <th className="py-2 pr-4 text-left font-semibold" style={ink}></th>
                      <th className="py-2 pr-4 text-left font-semibold" style={ink}>Appraisal</th>
                      <th className="py-2 text-left font-semibold" style={ink}>Home inspection</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/50">
                    <tr><td className="py-2 pr-4 font-semibold" style={ink}>Purpose</td><td className="py-2 pr-4">Determine market value</td><td className="py-2">Evaluate physical condition</td></tr>
                    <tr><td className="py-2 pr-4 font-semibold" style={ink}>Required by</td><td className="py-2 pr-4">Lender (for mortgage)</td><td className="py-2">Buyer (optional but recommended)</td></tr>
                    <tr><td className="py-2 pr-4 font-semibold" style={ink}>Licensed by</td><td className="py-2 pr-4">State appraiser board</td><td className="py-2">State home inspector board</td></tr>
                    <tr><td className="py-2 pr-4 font-semibold" style={ink}>Focus</td><td className="py-2 pr-4">Market data, comparables, value</td><td className="py-2">Roof, foundation, HVAC, plumbing, electrical</td></tr>
                    <tr><td className="py-2 pr-4 font-semibold" style={ink}>Typical cost</td><td className="py-2 pr-4">$400–$800 (NJ residential)</td><td className="py-2">$350–$600</td></tr>
                    <tr><td className="py-2 pr-4 font-semibold" style={ink}>Result</td><td className="py-2 pr-4">Appraised value opinion</td><td className="py-2">Condition report with repair recommendations</td></tr>
                  </tbody>
                </table>
              </div>
              <p className="mt-2">Both services protect you — the appraisal ensures you don't overpay, while the inspection ensures you know what you're buying.</p>
            </GuideCard>
          </div>
        </Container>
      </section>

      {/* ──────────────── PROSPECTIVE APPRAISERS SECTION ──────────────── */}
      <section id="appraisers" className="relative overflow-hidden scroll-mt-20" style={{ background: 'var(--g5-soft)', borderTop: `1px solid ${line}`, borderBottom: `1px solid ${line}` }}>
        <Container className="relative py-16 md:py-24">
          <div className="max-w-2xl">
            <LabelChip>Career resources</LabelChip>
            <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight md:text-4xl" style={ink}>
              For prospective appraisers.
            </h2>
            <p className="mt-4 text-base leading-relaxed" style={sub}>
              Start your career in real estate appraisal — the path, the credentials, and how to find a mentor.
            </p>
          </div>

          <div className="mt-10 space-y-4">
            <GuideCard
              icon={<BookOpen className="h-5 w-5" strokeWidth={1.75} />}
              title="How to become an appraiser in NJ"
              blurb="The five-step path from coursework to credential."
              defaultOpen
            >
              <p>Becoming a licensed real estate appraiser in New Jersey follows a structured path set by the NJ Board of Real Estate Appraisers and the Appraiser Qualifications Board (AQB).</p>
              <div className="mt-3 space-y-4">
                {[
                  { n: 1, t: 'Complete required education', d: <>Start with the 75-hour Basic Appraisal Principles and Procedures course from an AQB-approved provider. This can be completed online or in-person and typically takes 2–3 weeks.</> },
                  { n: 2, t: 'Find a supervisory appraiser', d: <>You'll need a Certified Residential or Certified General appraiser willing to supervise your work. They must have at least 3 years of experience and a clean disciplinary record. This is often the hardest step — use our <Link to="/directory?tab=supervisors" className="aura-link underline">Find a Supervisor</Link> page to connect with available supervisors.</> },
                  { n: 3, t: 'Apply for trainee license', d: <>Submit your application to the NJ Division of Consumer Affairs with your education certificates and supervisory appraiser agreement. The application fee is approximately $160.</> },
                  { n: 4, t: 'Accumulate experience hours', d: <>Work under your supervisor completing appraisals and logging hours. Requirements vary by credential level: 1,000 hours for Licensed Residential, 1,500 for Certified Residential, and 3,000 for Certified General.</> },
                  { n: 5, t: 'Pass the state exam', d: <>Once you've met the education and experience requirements, take and pass the national uniform licensing exam administered by PSI. Then apply for your full credential.</> },
                ].map((step) => (
                  <div key={step.n} className="flex gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold" style={{ background: tint, color: accent }}>{step.n}</div>
                    <div>
                      <p className="font-semibold" style={ink}>{step.t}</p>
                      <p>{step.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </GuideCard>

            <GuideCard
              icon={<Award className="h-5 w-5" strokeWidth={1.75} />}
              title="NJ licensing tiers explained"
              blurb="Four credential levels, from trainee to certified general."
            >
              <p>New Jersey offers four credential levels, each with different property type and complexity authorizations:</p>
              <div className="mt-2 space-y-3">
                <div className="rounded-2xl p-4" style={{ ...softBox, borderLeft: `3px solid ${line}` }}>
                  <p className="font-semibold" style={ink}>Licensed trainee</p>
                  <p>75 hours education. Must work under direct supervision. Cannot sign reports independently. The entry point for all new appraisers.</p>
                </div>
                <div className="rounded-2xl p-4" style={{ ...softBox, borderLeft: '3px solid color-mix(in srgb, var(--g5-accent) 30%, transparent)' }}>
                  <p className="font-semibold" style={ink}>Licensed residential</p>
                  <p>150 hours education + 1,000 experience hours over 6+ months. Can appraise non-complex 1–4 unit residential properties up to $1,000,000 and complex properties up to $250,000.</p>
                </div>
                <div className="rounded-2xl p-4" style={{ ...softBox, borderLeft: '3px solid color-mix(in srgb, var(--g5-accent) 50%, transparent)' }}>
                  <p className="font-semibold" style={ink}>Certified residential</p>
                  <p>200 hours education + 1,500 experience hours over 12+ months + associate degree or equivalent. Can appraise all 1–4 unit residential properties regardless of value or complexity. The most common credential for full-time residential appraisers.</p>
                </div>
                <div className="rounded-2xl p-4" style={{ ...softBox, borderLeft: '3px solid color-mix(in srgb, var(--g5-accent) 70%, transparent)' }}>
                  <p className="font-semibold" style={ink}>Certified general</p>
                  <p>300 hours education + 3,000 experience hours (1,500 must be non-residential) over 18+ months + bachelor's degree or equivalent. Can appraise all property types including commercial, industrial, and agricultural.</p>
                </div>
              </div>
            </GuideCard>

            <GuideCard
              icon={<Users className="h-5 w-5" strokeWidth={1.75} />}
              title="Finding a supervisory appraiser"
              blurb="What to look for in a mentor, and where to look."
            >
              <p>Your supervisory appraiser is your mentor, teacher, and gateway into the profession. Finding the right match is crucial for your development.</p>
              <div className="mt-3 grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl p-4" style={softBox}>
                  <p className="mb-2 font-semibold" style={ink}>What to look for</p>
                  <ul className="space-y-1.5">
                    <Bullet>An appraiser in your target geographic area (inspections are local)</Bullet>
                    <Bullet>Someone who specializes in the property types you want to learn</Bullet>
                    <Bullet>A willingness to actually teach — ask about their training philosophy</Bullet>
                    <Bullet>Reasonable fee splits (common splits are 40/60 or 50/50)</Bullet>
                    <Bullet>Good standing with the NJ Board (no disciplinary actions)</Bullet>
                  </ul>
                </div>
                <div className="rounded-2xl p-4" style={softBox}>
                  <p className="mb-2 font-semibold" style={ink}>Where to look</p>
                  <ul className="space-y-1.5">
                    <Bullet>Our <Link to="/directory?tab=supervisors" className="aura-link underline">Find a Supervisor</Link> page — appraisers open to mentoring</Bullet>
                    <Bullet>NJ chapter meetings of the Appraisal Institute or ASA</Bullet>
                    <Bullet>LinkedIn — search certified appraisers nearby and reach out professionally</Bullet>
                    <Bullet>Local real estate networking events and appraisal conferences</Bullet>
                  </ul>
                </div>
              </div>
            </GuideCard>

            <GuideCard
              icon={<Clock className="h-5 w-5" strokeWidth={1.75} />}
              title="Continuing education requirements"
              blurb="What it takes to keep your NJ credential current."
            >
              <p>Once licensed, NJ appraisers must complete continuing education (CE) every two-year renewal cycle to maintain their credential.</p>
              <div className="mt-2 space-y-3">
                <div className="rounded-2xl p-4" style={softBox}>
                  <p className="mb-1 font-semibold" style={ink}>Current requirements (2025–2027 cycle)</p>
                  <ul className="mt-1 space-y-1.5">
                    <Bullet><strong style={ink}>28 total hours</strong> for certified appraisers (24 for trainees)</Bullet>
                    <Bullet><strong style={ink}>7-hour USPAP update</strong> — mandatory national course covering current standards</Bullet>
                    <Bullet><strong style={ink}>4-hour NJ law module</strong> — covers NJ-specific regulations</Bullet>
                    <Bullet><strong style={ink}>17 hours elective</strong> — topics relevant to your practice (market analysis, commercial techniques, report writing, etc.)</Bullet>
                  </ul>
                </div>
                <div className="rounded-2xl p-4" style={softBox}>
                  <p className="font-semibold" style={ink}>Online options</p>
                  <p>Up to 50% of CE hours can be completed through live (synchronous) online courses. Self-paced (asynchronous) courses are capped at 14 hours. All courses must be from providers approved by the NJ Board.</p>
                </div>
              </div>
            </GuideCard>
          </div>
        </Container>
      </section>

      {/* ──────────────── INDUSTRY LINKS ──────────────── */}
      <section className="relative overflow-hidden">
        <Container className="py-16 md:py-24">
          <div className="text-center">
            <CourseDivider className="mb-6" />
            <LabelChip>Around the industry</LabelChip>
            <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight md:text-4xl" style={ink}>
              Industry links.
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed" style={sub}>
              Essential organizations and regulatory bodies for the NJ appraisal community.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            <ExternalLinkCard
              href="https://www.njconsumeraffairs.gov/rea/"
              title="NJ Board of Real Estate Appraisers"
              description="State licensing authority — applications, renewals, CE approval, complaint investigations, and disciplinary actions."
            />
            <ExternalLinkCard
              href="https://www.appraisalfoundation.org"
              title="The Appraisal Foundation"
              description="Congressionally authorized source of USPAP standards and minimum appraiser qualification criteria."
            />
            <ExternalLinkCard
              href="https://www.asc.gov"
              title="Appraisal Subcommittee (ASC)"
              description="Federal oversight of state appraiser regulatory programs. Maintains the National Registry of licensed appraisers."
            />
            <ExternalLinkCard
              href="https://www.appraisalinstitute.org"
              title="Appraisal Institute"
              description="Largest professional organization for appraisers. Offers MAI, SRA, and AI-GRS designations plus education and market data."
            />
            <ExternalLinkCard
              href="https://www.appraisers.org"
              title="American Society of Appraisers"
              description="Multi-discipline professional organization covering real estate, business valuation, and personal property appraisal."
            />
            <ExternalLinkCard
              href="https://www.huduser.gov/portal/datasets/fmr.html"
              title="HUD Fair Market Rents"
              description="Annual fair market rent data by metro area — useful for income approach analysis and rental market research."
            />
          </div>
        </Container>
      </section>

      {/* CTA */}
      <CTABanner
        heading="Ready to get started?"
        subtext="Join the modern appraisal platform — flexible pricing that scales with your practice."
        secondaryLabel="View Pricing"
        secondaryHref="/pricing"
      />

      <style>{`.res-card { transition: transform .18s ease, border-color .18s ease; } .res-card:hover { transform: translateY(-3px); border-color: color-mix(in srgb, var(--g5-key) 55%, var(--g5-line)); }`}</style>
    </PublicPageLayout>
  );
}
